import { type LearnSearchResults } from '@module/mdrt-org/shared/utils/data/graphql/data/get-learn-search-results';

import { type useSearchPageSearchParameters } from '../../../utils/hooks/use-search-page-search-parameters';
import { type AdvancedFilterOptions, type FilterOption } from '../types/advanced-filter-form-data';

type LearnSearchFacetField = Exclude<
  LearnSearchResults['facets'],
  null | undefined
>['DisplayedDateYear'];

const transformFacetItemsToFilterOptions = (facetItems: LearnSearchFacetField) => {
  const filterOptions = (facetItems || []).map((item) => {
    if (!item || !item.name || !item.count) {
      return null;
    }

    return {
      count: item.count,
      defaultValue: false,
      name: item.name,
    };
  });
  return filterOptions.filter(Boolean) as FilterOption[];
};

const addHistoricFilterOption = (filterOptions: FilterOption[], optionName: string) => {
  filterOptions.push({ count: 0, defaultValue: true, name: optionName });
};

const addHistoricOptionsFromAppliedSearchParameters = (
  filterOptions: FilterOption[],
  searchParameterValue: string[] | undefined
) => {
  for (const optionName of searchParameterValue || []) {
    const option = filterOptions.find(({ name }) => name === optionName);
    if (option) {
      option.defaultValue = true;
    } else {
      addHistoricFilterOption(filterOptions, optionName);
    }
  }

  return filterOptions;
};

const getFilterSectionOptions = ({
  facetItems,
  searchParameterValue,
}: {
  facetItems: LearnSearchFacetField;
  searchParameterValue: string[] | undefined;
}) => {
  const options = transformFacetItemsToFilterOptions(facetItems);
  return addHistoricOptionsFromAppliedSearchParameters(options, searchParameterValue);
};

const getLocalCalendarYear = ({
  gregorianYear,
  yearFormatter,
}: {
  gregorianYear: string;
  yearFormatter: Intl.DateTimeFormat;
}): string => {
  const yearDisplayString = yearFormatter.format(new Date(Number(gregorianYear), 0));

  const yearString = yearDisplayString.match(/\d+/u)?.[0];
  if (!yearString) {
    return gregorianYear;
  }

  return yearString;
};

export const getFilterOptionsWithDefaultValues = ({
  facets,
  locale,
  searchParameters,
}: {
  facets: LearnSearchResults['facets'];
  locale: string;
  searchParameters: ReturnType<typeof useSearchPageSearchParameters>;
}): AdvancedFilterOptions => {
  const yearFormatter = new Intl.DateTimeFormat(locale, { year: 'numeric' });

  return {
    savedContent: [
      {
        defaultValue: searchParameters.savedContent
          ? Boolean(searchParameters.savedContent)
          : false,
        name: 'savedContent',
      },
    ],
    source: getFilterSectionOptions({
      facetItems: facets?.ExpandedSources?.Name,
      searchParameterValue: searchParameters.source,
    }),
    topic: getFilterSectionOptions({
      facetItems: facets?.ExpandedTopics?.Name,
      searchParameterValue: searchParameters.topic,
    }),
    year: getFilterSectionOptions({
      facetItems: facets?.DisplayedDateYear,
      searchParameterValue: searchParameters.year,
    })
      .sort((optionA, optionB) => Number(optionB.name) - Number(optionA.name))
      .map((yearFilterOption) => ({
        ...yearFilterOption,
        displayName: getLocalCalendarYear({
          gregorianYear: yearFilterOption.name,
          yearFormatter,
        }),
      })),
  };
};
